/**
 * Local Storage Service
 * Manages local storage for non-logged-in users, including address history
 * and booking details for repeat anonymous bookings.
 */

const LOCAL_ADDRESS_HISTORY_KEY = 'ridequest_address_history';
const LOCAL_BOOKING_DETAILS_KEY = 'ridequest_booking_details';
const MAX_LOCAL_ADDRESSES = 10;
const BOOKING_DETAILS_EXPIRY_DAYS = 90;

export interface LocalAddressHistory {
  id: string;
  pickup: string;
  destination: string;
  timestamp: number;
}

/**
 * Get address history from localStorage
 * @returns Array of address history entries, sorted by most recent first
 */
export function getLocalAddressHistory(): LocalAddressHistory[] {
  try {
    const stored = localStorage.getItem(LOCAL_ADDRESS_HISTORY_KEY);
    if (!stored) {
      return [];
    }
    const history: LocalAddressHistory[] = JSON.parse(stored);
    // Sort by timestamp descending (most recent first)
    return history.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('Error reading address history from localStorage:', error);
    return [];
  }
}

/**
 * Save a pickup/destination pair to localStorage
 * Avoids duplicates and limits to MAX_LOCAL_ADDRESSES entries
 * @param pickup - Pickup location string
 * @param destination - Destination location string
 */
export function saveLocalAddressHistory(pickup: string, destination: string): void {
  try {
    if (!pickup || !destination) {
      return;
    }

    const history = getLocalAddressHistory();

    // Check for duplicate (same pickup and destination)
    const isDuplicate = history.some(
      entry => entry.pickup === pickup && entry.destination === destination
    );

    if (isDuplicate) {
      // Update timestamp of existing entry instead of adding duplicate
      const updatedHistory = history.map(entry => {
        if (entry.pickup === pickup && entry.destination === destination) {
          return { ...entry, timestamp: Date.now() };
        }
        return entry;
      });
      localStorage.setItem(LOCAL_ADDRESS_HISTORY_KEY, JSON.stringify(updatedHistory));
      return;
    }

    // Create new entry
    const newEntry: LocalAddressHistory = {
      id: `local_${Date.now()}`,
      pickup,
      destination,
      timestamp: Date.now()
    };

    // Add to beginning and limit to MAX_LOCAL_ADDRESSES
    const updatedHistory = [newEntry, ...history].slice(0, MAX_LOCAL_ADDRESSES);
    localStorage.setItem(LOCAL_ADDRESS_HISTORY_KEY, JSON.stringify(updatedHistory));
  } catch (error) {
    console.error('Error saving address history to localStorage:', error);
  }
}

/**
 * Clear all address history from localStorage
 */
export function clearLocalAddressHistory(): void {
  try {
    localStorage.removeItem(LOCAL_ADDRESS_HISTORY_KEY);
  } catch (error) {
    console.error('Error clearing address history from localStorage:', error);
  }
}

/**
 * Merge local address history with remote (database) history
 * Removes duplicates and sorts by most recent
 * @param remoteHistory - Address history from database
 * @returns Merged and deduplicated address history
 */
export function mergeWithRemoteHistory(
  remoteHistory: Array<{ id: string; pickup: string; destination: string }>
): Array<{ id: string; pickup: string; destination: string }> {
  const localHistory = getLocalAddressHistory();

  // Create a map to track unique pickup/destination combinations
  const uniqueMap = new Map<string, { id: string; pickup: string; destination: string; timestamp?: number }>();

  // Add remote history first (these are from database, so they're confirmed bookings)
  remoteHistory.forEach(entry => {
    const key = `${entry.pickup}|${entry.destination}`;
    uniqueMap.set(key, entry);
  });

  // Add local history entries that don't exist in remote
  localHistory.forEach(entry => {
    const key = `${entry.pickup}|${entry.destination}`;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, { id: entry.id, pickup: entry.pickup, destination: entry.destination });
    }
  });

  return Array.from(uniqueMap.values());
}

// ============================================
// Booking Details Storage for Anonymous Users
// ============================================

/**
 * Stores passenger and booking preferences for repeat anonymous bookings
 */
export interface LocalBookingDetails {
  passengerName: string;
  companyName?: string;
  countryCode: string;
  passengerMobile: string;
  passengerEmail: string;
  paymentMethod?: string;
  communicationPrefs?: {
    voice: boolean;
    sms: boolean;
    whatsapp: boolean;
    email: boolean;
  };
  lastUpdated: number;
}

/**
 * Save booking details to localStorage for anonymous users
 * Allows repeat bookings without re-entering all information
 */
export function saveLocalBookingDetails(
  details: Omit<LocalBookingDetails, 'lastUpdated'>
): void {
  try {
    const data: LocalBookingDetails = {
      ...details,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(LOCAL_BOOKING_DETAILS_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving booking details to localStorage:', error);
  }
}

/**
 * Get saved booking details from localStorage
 * Returns null if no data exists or if data has expired (90 days)
 */
export function getLocalBookingDetails(): LocalBookingDetails | null {
  try {
    const stored = localStorage.getItem(LOCAL_BOOKING_DETAILS_KEY);
    if (!stored) {
      return null;
    }

    const data: LocalBookingDetails = JSON.parse(stored);

    // Check for expiry
    const expiryMs = BOOKING_DETAILS_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - data.lastUpdated > expiryMs) {
      localStorage.removeItem(LOCAL_BOOKING_DETAILS_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading booking details from localStorage:', error);
    return null;
  }
}

/**
 * Clear saved booking details from localStorage
 */
export function clearLocalBookingDetails(): void {
  try {
    localStorage.removeItem(LOCAL_BOOKING_DETAILS_KEY);
  } catch (error) {
    console.error('Error clearing booking details from localStorage:', error);
  }
}

export const localStorageService = {
  getLocalAddressHistory,
  saveLocalAddressHistory,
  clearLocalAddressHistory,
  mergeWithRemoteHistory,
  saveLocalBookingDetails,
  getLocalBookingDetails,
  clearLocalBookingDetails,
};

export default localStorageService;
